/**
 * product-group-swatches.js
 *
 * Groups products by tag product-group--{name}.
 * On PLP: fetches sibling handles via Storefront API GraphQL,
 *         pre-fetches their card HTML via Section Rendering API,
 *         renders colour swatches and swaps cards on click.
 * On PDP: same API calls, renders <a href> colour swatches (page navigation).
 */
if (!window.GroupSwatches) {
  const STOREFRONT_API_VERSION = '2026-01';
  const SECTION_ID = 'product-grid-item';
  const VISIBLE_LIMIT = 6;

  // tag -> handles[] (after resolution)
  const groupHandles = new Map();
  // tag -> Promise<handles[]> (while fetching)
  const groupFetching = new Map();
  // handle -> Promise<string> (HTML, while fetching)
  const cardFetching = new Map();
  // handle -> string (resolved HTML, for synchronous access)
  const cardResolved = new Map();

  const getToken = () => window.theme?.storefrontAccessToken ?? '';
  const getDomain = () => window.theme?.shopDomain ?? location.hostname;
  const getShopUrl = () => window.shopUrl ?? '';

  /**
   * Extract the inner HTML of the first .color-var.active element from
   * a Section Rendering HTML string. Used to fill in swatch colours.
   */
  const extractSwatchContent = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.color-var.active')?.innerHTML ?? '';
  };

  const normalizeGroupHandles = (handles) => {
    return Array.from(new Set((handles ?? []).filter(Boolean)));
  };

  const getVisibleHandles = (handles, currentHandle) => {
    const ordered = normalizeGroupHandles(handles);
    const visible = ordered.slice(0, VISIBLE_LIMIT);

    if (currentHandle && ordered.includes(currentHandle) && !visible.includes(currentHandle) && visible.length > 0) {
      visible[visible.length - 1] = currentHandle;
    }

    return Array.from(new Set(visible));
  };

  const getSwatchMarkup = async (handle, fallbackMarkup = '') => {
    try {
      const cachedHtml = cardResolved.get(handle);
      if (cachedHtml) {
        return extractSwatchContent(cachedHtml) || fallbackMarkup;
      }

      const html = await prefetchCard(handle);
      return extractSwatchContent(html) || fallbackMarkup;
    } catch (err) {
      console.error('[GroupSwatches] getSwatchMarkup error for', handle, err);
      return fallbackMarkup;
    }
  };

  const fetchGroup = async (tag) => {
    if (groupHandles.has(tag)) {
      return groupHandles.get(tag);
    }
    if (groupFetching.has(tag)) {
      return groupFetching.get(tag);
    }

    const token = getToken();
    const domain = getDomain();

    if (!token || !domain) {
      console.warn('[GroupSwatches] Missing storefrontAccessToken or shopDomain. Set it in Theme Editor -> Developer.');
      return [];
    }

    const graphqlQuery = `query ProductsByTag($query: String!) {
      products(first: 20, query: $query) {
        edges {
          node {
            handle
            tags
          }
        }
      }
    }`;
    const storefrontQuery = `tag:"${String(tag).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

    const createGroupRequest = async () => {
      try {
        const res = await fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query: graphqlQuery,
            variables: { query: storefrontQuery },
          }),
        });

        if (!res.ok) throw new Error(`Storefront API ${res.status}`);

        const data = await res.json();
        const edges = data?.data?.products?.edges ?? [];
        const handles = edges
          .map((edge) => edge?.node)
          .filter((node) => node?.handle && Array.isArray(node.tags) && node.tags.includes(tag))
          .map((node) => node.handle);
        groupHandles.set(tag, handles);
        return handles;
      } catch (err) {
        console.error('[GroupSwatches] fetchGroup error:', err);
        return [];
      } finally {
        groupFetching.delete(tag);
      }
    };

    const request = createGroupRequest();

    groupFetching.set(tag, request);
    return request;
  };

  const prefetchCard = async (handle) => {
    if (cardResolved.has(handle)) {
      return cardResolved.get(handle);
    }
    if (cardFetching.has(handle)) {
      return cardFetching.get(handle);
    }

    const createCardRequest = async () => {
      try {
        const res = await fetch(`${getShopUrl()}/products/${handle}?sections=${SECTION_ID}`);
        if (!res.ok) throw new Error(`Section Rendering ${res.status}`);

        const json = await res.json();
        const html = json?.[SECTION_ID] ?? '';
        cardResolved.set(handle, html);
        return html;
      } catch (err) {
        console.error('[GroupSwatches] prefetchCard error for', handle, err);
        return '';
      } finally {
        cardFetching.delete(handle);
      }
    };

    const request = createCardRequest();

    cardFetching.set(handle, request);
    return request;
  };

  /**
   * Render/refresh colour swatches inside an .inner-product-grid element.
   * The server-rendered .color-var.active for currentHandle is preserved;
   * all other siblings get .color-var.click[data-handle] elements appended.
   */
  const renderSwatches = async (innerGrid, handles, currentHandle) => {
    const container = innerGrid.querySelector('.main-color-variant');
    if (!container) return;

    const normalizedHandles = normalizeGroupHandles(handles);
    const visibleHandles = getVisibleHandles(handles, currentHandle);

    const colorCount = innerGrid.querySelector('.color-count');
    if (colorCount) {
      const n = normalizedHandles.length;
      const one = window.variantStrings?.colour ?? 'colour';
      const many = window.variantStrings?.colours ?? 'colours';
      colorCount.textContent = `${n} ${n === 1 ? one : many}`;
    }

    const activeMarkup = container.querySelector('.color-var.active')?.innerHTML ?? '';
    const fragment = document.createDocumentFragment();
    const swatches = await Promise.all(visibleHandles.map(async (handle) => {
      const swatchEl = document.createElement('span');
      swatchEl.className = `color-var ${handle === currentHandle ? 'active' : 'click'}`;
      swatchEl.dataset.handle = handle;
      swatchEl.innerHTML = handle === currentHandle
        ? activeMarkup
        : await getSwatchMarkup(handle);

      return swatchEl;
    }));

    swatches.forEach((swatchEl) => {
      if (swatchEl.innerHTML) fragment.appendChild(swatchEl);
    });

    if (normalizedHandles.length > VISIBLE_LIMIT) {
      const more = document.createElement('span');
      more.className = 'more';
      more.textContent = `+${normalizedHandles.length - VISIBLE_LIMIT}`;
      fragment.appendChild(more);
    }

    container.replaceChildren(fragment);
  };

  const swapCard = async (snippetEl, newHandle, handles) => {
    const html = await prefetchCard(newHandle);
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInnerGrid = doc.querySelector('.inner-product-grid');
    if (!newInnerGrid) return;

    const oldInnerGrid = snippetEl.querySelector('.inner-product-grid.active');
    if (!oldInnerGrid) return;

    snippetEl.querySelectorAll('.color-var').forEach((variantEl) => {
      variantEl.classList.remove('active');
      if (!variantEl.classList.contains('click')) variantEl.classList.add('click');
    });

    const clickedSwatch = snippetEl.querySelector(`.color-var[data-handle="${newHandle}"]`);
    if (clickedSwatch) {
      clickedSwatch.classList.remove('click');
      clickedSwatch.classList.add('active');
    }

    oldInnerGrid.replaceWith(newInnerGrid);
    snippetEl.dataset.id = newHandle;
    await renderSwatches(newInnerGrid, handles, newHandle);

    const newActiveInCard = newInnerGrid.querySelector('.color-var.active');
    if (newActiveInCard && clickedSwatch) {
      clickedSwatch.innerHTML = newActiveInCard.innerHTML;
    }

    if (typeof $th_refreshWishlistItems === 'function') {
      $th_refreshWishlistItems();
    }
  };

  const processTagCards = async (cardEls, tag) => {
    cardEls.forEach((cardEl) => {
      if (cardEl.dataset.id) void prefetchCard(cardEl.dataset.id);
    });

    const handles = await fetchGroup(tag);
    if (!handles || handles.length < 2) return;

    cardEls.forEach((cardEl) => {
      const innerGrid = cardEl.querySelector('.inner-product-grid.active');
      if (!innerGrid) return;

      const currentHandle = cardEl.dataset.id;
      const normalizedHandles = normalizeGroupHandles(handles);
      void renderSwatches(innerGrid, normalizedHandles, currentHandle);

      normalizedHandles.forEach((handle) => {
        if (handle !== currentHandle) void prefetchCard(handle);
      });
    });
  };

  const init = (root = document) => {
    const cards = Array.from(root.querySelectorAll('.snippet-product-grid[data-group-tag]'));
    const tagMap = new Map();

    cards.forEach((card) => {
      const tag = card.dataset.groupTag;
      if (!tag) return;
      if (!tagMap.has(tag)) tagMap.set(tag, []);
      tagMap.get(tag).push(card);
    });

    tagMap.forEach((cardEls, tag) => {
      processTagCards(cardEls, tag).catch((err) => {
        console.error('[GroupSwatches] init error:', err);
      });
    });
  };

  const initPdp = () => {
    const container = document.querySelector('.snippet-color-selector .main-color-variant[data-group-tag]');
    if (!container) return;

    const tag = container.dataset.groupTag;
    if (!tag) return;

    const currentHandle = container.dataset.currentHandle;

    const runPdpInit = async () => {
      const handles = await fetchGroup(tag);
      if (!handles || handles.length < 2) return;

      const normalizedHandles = normalizeGroupHandles([...(handles ?? []), currentHandle]);
      const activeMarkup = container.querySelector('.color-var.active')?.innerHTML ?? '';
      const fragment = document.createDocumentFragment();

      const topInfo = container.closest('.snippet-color-selector');
      if (topInfo) {
        const countEl = topInfo.querySelector('.color-count');
        if (countEl) {
          const n = normalizedHandles.length;
          countEl.textContent = `${n} ${n === 1 ? 'colour' : 'colours'}`;
        }
      }

      const links = await Promise.all(normalizedHandles.map(async (handle) => {
        const link = document.createElement('a');
        link.href = '/products/' + handle;
        link.className = `color-var ${handle === currentHandle ? 'active' : ''}`.trim();
        link.dataset.handle = handle;

        link.innerHTML = handle === currentHandle
          ? activeMarkup
          : await getSwatchMarkup(handle);

        return link;
      }));

      links.forEach((link) => {
        if (link.innerHTML) fragment.appendChild(link);
      });

      container.replaceChildren(fragment);
    };

    runPdpInit().catch((err) => {
      console.error('[GroupSwatches] initPdp error:', err);
    });
  };

  document.addEventListener('click', async (e) => {
    const swatch = e.target?.closest('.snippet-product-grid .color-var.click[data-handle]');
    if (!swatch) return;

    e.preventDefault();

    const snippetEl = swatch.closest('.snippet-product-grid');
    if (!snippetEl) return;

    const newHandle = swatch.dataset.handle;
    const tag = snippetEl.dataset.groupTag;
    if (!newHandle || !tag) return;

    const cached = groupHandles.get(tag);
    if (cached) {
      await swapCard(snippetEl, newHandle, cached);
      return;
    }

    try {
      const handles = await fetchGroup(tag);
      await swapCard(snippetEl, newHandle, handles);
    } catch (err) {
      console.error('[GroupSwatches] click handler error:', err);
    }
  });

  // For PLP, init is called externally after dynamic render.
  // For PDP, run now if DOM is ready, otherwise wait for DOMContentLoaded.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (document.querySelector('.snippet-color-selector')) initPdp();
    });
  } else if (document.querySelector('.snippet-color-selector')) {
    initPdp();
  }

  window.GroupSwatches = { init, initPdp };
  window.dispatchEvent(new CustomEvent('groupswatches:ready', { detail: window.GroupSwatches }));
}
